// frontend/src/pages/PrescriptionPage.tsx
import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles, Heart, ArrowRight, Home, RefreshCw,
  Film, Download, X
} from 'lucide-react';
import { PRESCRIPTION_QUESTIONS, RESULT_MESSAGES } from '../utils/PrescriptionData';
import { moviesAPI } from '../services/api';

// ─────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────
interface Movie {
  id: number;
  title: string;
  overview?: string;
  posterUrl?: string;
  releaseYear?: number;
  voteAverage?: number;
  season?: string;
  holiday?: string;
  moods?: string[];
  tropes?: string[];
}

interface Answer {
  questionId: number;
  optionId: string;
  mood?: string;
  trope?: string;
  season?: string;
  era?: string;
  character?: string;
}

interface MovieMatch {
  movie: Movie;
  score: number;
}

// ─────────────────────────────────────────────
// ANSWER → API PARAM MAPPING
// Changed to ROMCOM prescriptor from PRESCRIPTION_QUESTIONS 
// ─────────────────────────────────────────────
const MOOD_MAP: Record<string, string> = {
  cozy: 'cozy',
  romantic: 'feel_good',
  funny: 'funny',
  emotional: 'emotional',
};

const TROPE_MAP: Record<string, string> = {
  'fake-dating': 'Fake Dating',
  'enemies-lovers': 'Enemies to Lovers',
  'friends-lovers': 'Friends to Lovers',
  'holiday-romance': 'Holiday Romance',
};

const SEASON_MAP: Record<string, string> = {
  city: 'winter',
  'small-town': 'autumn',
  international: 'summer',
  beach: 'summer',
};

const ERA_MAP: Record<string, { decade: number }> = {
  '80s': { decade: 1980 },
  '90s': { decade: 1990 },
  '2000s': { decade: 2000 },
  modern: { decade: 2010 },
};

const CHARACTER_MOOD_MAP: Record<string, string> = {
  'hopeless-romantic': 'feel_good',
  'sarcastic-funny': 'funny',
  'quirky-creative': 'emotional',
  'strong-independent': 'comfort',
};

// ─────────────────────────────────────────────
// RESULT CARD COMPONENT
// ─────────────────────────────────────────────
const ResultCard: React.FC<{
  topMovie: Movie;
  matchScore: number;
  matchMessage: typeof RESULT_MESSAGES.perfect;
  answers: Answer[];
  onClose: () => void;
}> = ({ topMovie, matchScore, matchMessage, answers, onClose }) => {
  const cardRef = useRef<HTMLDivElement>(null);

  const handleDownload = async () => {
    if (!cardRef.current) return;
    try {
      // @ts-ignore
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(cardRef.current, { scale: 2, useCORS: true } as any);
      
      const link = document.createElement('a');
      link.download = `my-romcom-prescriptor-${topMovie.title.replace(/\s+/g, '-').toLowerCase()}.png`;
      link.href = canvas.toDataURL();
      link.click();
    } catch {
      alert('Could not generate image. Try screenshotting instead! 💕');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        onClick={e => e.stopPropagation()}
        className="bg-white rounded-3xl shadow-2xl max-w-sm w-full overflow-hidden my-4"
      >
        {/* Printable card */}
        <div
          ref={cardRef}
          className="bg-gradient-to-br from-pink-400 via-rose-400 to-red-500 p-8 text-white relative overflow-hidden"
        >
          {/* Decorative hearts */}
          <div className="absolute top-2 left-4 text-white/20 text-6xl select-none">💕</div>
          <div className="absolute bottom-4 right-4 text-white/20 text-5xl select-none">💖</div>
          <div className="absolute top-1/2 left-2 text-white/10 text-4xl select-none">❤️</div>

          {/* Title */}
          <div className="text-center mb-6 relative">
            <div className="flex justify-center gap-2 mb-3">
              {[...Array(5)].map((_, i) => (
                <Heart key={i} className="w-5 h-5 fill-current text-white/80" />
              ))}
            </div>
            <h2 className="text-2xl font-playfair font-bold">For the Love of Romcoms</h2>
            <p className="text-white/80 text-sm mt-1">Your Romcom Prescription</p>
          </div>

          {/* Match score */}
          <div className="text-center mb-6">
            <div className="text-7xl font-bold mb-1">{matchScore}%</div>
            <div className="text-xl font-semibold opacity-90">{matchMessage.title}</div>
            <div className="text-sm opacity-75 mt-1">{matchMessage.message}</div>
          </div>

          {/* Movie */}
          <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-5 mb-6">
            <div className="flex gap-4 items-center">
              {topMovie.posterUrl ? (
                <img
                  src={topMovie.posterUrl}
                  alt={topMovie.title}
                  className="w-16 h-24 object-cover rounded-xl shadow-lg flex-shrink-0"
                  crossOrigin="anonymous"
                />
              ) : (
                <div className="w-16 h-24 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Film className="w-8 h-8 text-white" />
                </div>
              )}
              <div>
                <p className="text-xs uppercase tracking-wider opacity-75 mb-1">Your Prescription</p>
                <h3 className="text-xl font-bold leading-tight">{topMovie.title}</h3>
                {topMovie.releaseYear && (
                  <p className="text-white/70 text-sm">({topMovie.releaseYear})</p>
                )}
                {topMovie.moods && topMovie.moods.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {topMovie.moods.slice(0, 2).map(mood => (
                      <span key={mood} className="text-xs bg-white/20 px-2 py-0.5 rounded-full capitalize">
                        {mood.replace('_', ' ')}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Personality tags from answers */}
          <div className="flex flex-wrap justify-center gap-2 mb-4">
            {answers.map((a, i) => {
              const q = PRESCRIPTION_QUESTIONS.find(q => q.id === a.questionId);
              const opt = q?.options.find(o => o.id === a.optionId);
              return opt ? (
                <span key={i} className="bg-white/20 text-white text-xs px-3 py-1 rounded-full">
                  {opt.emoji} {opt.text}
                </span>
              ) : null;
            })}
          </div>

          {/* Footer */}
          <p className="text-center text-xs opacity-50 mt-4">fortheloveofromcoms.com</p>
        </div>

        {/* Actions */}
        <div className="p-4 flex gap-3">
          <button
            onClick={handleDownload}
            className="flex-1 flex items-center justify-center gap-2 bg-romcom-red text-white py-3 rounded-xl font-medium hover:bg-red-600 transition-colors"
          >
            <Download className="w-5 h-5" />
            Save My Prescription Card
          </button>
          <button
            onClick={onClose}
            className="p-3 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ─────────────────────────────────────────────
// MAIN PRESCRIPTION PAGE
// ─────────────────────────────────────────────
const PrescriptionPage: React.FC = () => {
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [showResult, setShowResult] = useState(false);
  const [topMovies, setTopMovies] = useState<MovieMatch[]>([]);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showCard, setShowCard] = useState(false);

  const currentQ = PRESCRIPTION_QUESTIONS[currentQuestion];
  const progress = ((currentQuestion + 1) / PRESCRIPTION_QUESTIONS.length) * 100;

  // ── Handle answer selection ──
  const handleAnswer = (option: any) => {
    if (isAnimating) return;
    setIsAnimating(true);

    const newAnswer: Answer = {
      questionId: currentQ.id,
      optionId: option.id,
    };

    const newAnswers = [...answers, newAnswer];
    setAnswers(newAnswers);

    if (currentQuestion < PRESCRIPTION_QUESTIONS.length - 1) {
      setTimeout(() => {
        setCurrentQuestion(prev => prev + 1);
        setIsAnimating(false);
      }, 300);
    } else {
      calculateResults(newAnswers);
    }
  };

  // ── Calculate results using real API ──
  const calculateResults = async (finalAnswers: Answer[]) => {
    setIsLoading(true);

    try {
      // Extract preferences from answers
      const moodAnswer = finalAnswers.find(a => {
        const q = PRESCRIPTION_QUESTIONS.find(q => q.id === a.questionId);
        return q?.type === 'mood';
      });

      const tropeAnswer = finalAnswers.find(a => {
        const q = PRESCRIPTION_QUESTIONS.find(q => q.id === a.questionId);
        return q?.type === 'trope';
      });

      const settingAnswer = finalAnswers.find(a => {
        const q = PRESCRIPTION_QUESTIONS.find(q => q.id === a.questionId);
        return q?.type === 'setting';
      });

      const eraAnswer = finalAnswers.find(a => {
        const q = PRESCRIPTION_QUESTIONS.find(q => q.id === a.questionId);
        return q?.type === 'era';
      });

      const characterAnswer = finalAnswers.find(a => {
        const q = PRESCRIPTION_QUESTIONS.find(q => q.id === a.questionId);
        return q?.type === 'character';
      });

      // Build API params from answers
      const primaryMood = moodAnswer ? MOOD_MAP[moodAnswer.optionId] : undefined;
      const characterMood = characterAnswer ? CHARACTER_MOOD_MAP[characterAnswer.optionId] : undefined;
      const trope = tropeAnswer ? TROPE_MAP[tropeAnswer.optionId] : undefined;
      const season = settingAnswer ? SEASON_MAP[settingAnswer.optionId] : undefined;
      const era = eraAnswer ? ERA_MAP[eraAnswer.optionId] : undefined;

      // Fetch matched movies — primary query using mood + trope
      const params: Record<string, any> = {
        limit: 20,
        sortBy: 'rating',
        minRating: 6,
      };

      if (primaryMood) params.mood = primaryMood;
      if (trope) params.trope = trope;
      if (season) params.season = season;
      if (era) params.decade = era.decade;

      const response = await moviesAPI.getMovies(params);
      let movies: Movie[] = response.data.data.movies;

      // If not enough results, broaden the search
      if (movies.length < 3) {
        const broadResponse = await moviesAPI.getMovies({
          limit: 20,
          sortBy: 'rating',
          mood: characterMood || primaryMood,
        });
        movies = [...movies, ...broadResponse.data.data.movies];
      }

      // Remove duplicates
      const seen = new Set<number>();
      movies = movies.filter(m => {
        if (seen.has(m.id)) return false;
        seen.add(m.id);
        return true;
      });

      // Score each movie based on how well it matches all answers
      const scored: MovieMatch[] = movies.map(movie => {
        let score = 70; // Base score

        // Mood match
        if (primaryMood && movie.moods?.includes(primaryMood)) score += 15;
        if (characterMood && movie.moods?.includes(characterMood)) score += 10;

        // Trope match
        if (trope && movie.tropes?.some(t => t.toLowerCase().includes(trope.toLowerCase()))) score += 15;

        // Season match
        if (season && movie.season === season) score += 10;

        // Rating bonus
        if (movie.voteAverage && movie.voteAverage >= 7.5) score += 5;

        // Add some randomness so results vary
        score += Math.random() * 5;

        return { movie, score: Math.min(Math.round(score), 99) };
      });

      // Sort and take top 3
      const top3 = scored
        .sort((a, b) => b.score - a.score)
        .slice(0, 3);

      setTopMovies(top3);
      setIsLoading(false);
      setShowResult(true);
      setIsAnimating(false);
    } catch (err) {
      console.error('Failed to calculate results:', err);
      setIsLoading(false);
      setIsAnimating(false);
    }
  };

  const restartPrescription = () => {
    setCurrentQuestion(0);
    setAnswers([]);
    setShowResult(false);
    setTopMovies([]);
    setIsLoading(false);
    setShowCard(false);
  };

  const getMatchMessage = (score: number) => {
    if (score >= 90) return RESULT_MESSAGES.perfect;
    if (score >= 80) return RESULT_MESSAGES.great;
    if (score >= 70) return RESULT_MESSAGES.good;
    return RESULT_MESSAGES.decent;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-romcom-pink/10 to-romcom-blue/10">

      {/* Result card modal */}
      <AnimatePresence>
        {showCard && topMovies[0] && (
          <ResultCard
            topMovie={topMovies[0].movie}
            matchScore={topMovies[0].score}
            matchMessage={getMatchMessage(topMovies[0].score)}
            answers={answers}
            onClose={() => setShowCard(false)}
          />
        )}
      </AnimatePresence>

      <div className="container mx-auto px-4 pt-24 pb-16">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="flex justify-center mb-4">
            <Sparkles className="w-12 h-12 text-romcom-red" />
          </div>
          <h1 className="text-5xl md:text-6xl font-playfair font-bold text-gray-800 mb-4">
            Romcom Prescriptor
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Answer {PRESCRIPTION_QUESTIONS.length} quick questions to find your perfect romcom prescription !
          </p>
        </motion.div>

        {/* Progress Bar */}
        {!showResult && !isLoading && (
          <div className="max-w-2xl mx-auto mb-8">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>Question {currentQuestion + 1} of {PRESCRIPTION_QUESTIONS.length}</span>
              <span>{Math.round(progress)}% complete</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5 }}
                className="h-full bg-gradient-to-r from-romcom-pink to-romcom-red"
              />
            </div>
          </div>
        )}

        {/* Loading */}
        {isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-24"
          >
            <div className="flex justify-center gap-2 mb-6">
              {[...Array(5)].map((_, i) => (
                <motion.div
                  key={i}
                  animate={{ scale: [1, 1.3, 1], opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
                >
                  <Heart className="w-8 h-8 text-romcom-red fill-current" />
                </motion.div>
              ))}
            </div>
            <p className="text-2xl font-playfair font-bold text-gray-800 mb-2">
              Finding the perfect romcom for you...
            </p>
            <p className="text-gray-500">Searching through 2000+ romcoms just for you 💕</p>
          </motion.div>
        )}

        {/* Prescription Questions */}
        <AnimatePresence mode="wait">
          {!showResult && !isLoading && (
            <motion.div
              key={`question-${currentQuestion}`}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="max-w-2xl mx-auto"
            >
              <div className="bg-[#faf8f6] rounded-3xl shadow-xl p-8 mb-8 border border-romcom-pink/20">
                <div className="flex items-center mb-6">
                  <div className="w-10 h-10 bg-gradient-to-br from-romcom-pink to-romcom-red rounded-full flex items-center justify-center text-white font-bold mr-4 flex-shrink-0">
                    {currentQuestion + 1}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800">{currentQ.question}</h2>
                    {currentQ.description && (
                      <p className="text-gray-500 mt-1">{currentQ.description}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {currentQ.options.map((option, index) => (
                    <motion.button
                      key={option.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleAnswer(option)}
                      disabled={isAnimating}
                      className="p-6 text-left border-2 border-gray-200 rounded-2xl hover:border-romcom-red hover:bg-romcom-red/5 transition-all duration-300 group disabled:opacity-50"
                    >
                      <div className="flex items-center">
                        <span className="text-3xl mr-4">{option.emoji}</span>
                        <h3 className="font-bold text-lg text-gray-800 group-hover:text-romcom-red flex-1">
                          {option.text}
                        </h3>
                        <ArrowRight className="w-5 h-5 text-gray-300 group-hover:text-romcom-red ml-2" />
                      </div>
                    </motion.button>
                  ))}
                </div>
              </div>

              {currentQuestion > 0 && (
                <div className="text-center">
                  <button
                    onClick={() => {
                      setCurrentQuestion(prev => prev - 1);
                      setAnswers(prev => prev.slice(0, -1));
                    }}
                    className="text-romcom-red hover:underline"
                  >
                    ← Back to previous question
                  </button>
                </div>
              )}
            </motion.div>
          )}

          {/* Results */}
          {showResult && !isLoading && (
            <motion.div
              key="results"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="max-w-4xl mx-auto"
            >
              {/* Top match banner */}
              {topMovies[0] && (
                <div className="bg-gradient-to-r from-romcom-pink to-romcom-red rounded-3xl p-8 text-white mb-8 relative overflow-hidden">
                  {/* Decorative hearts */}
                  <div className="absolute top-4 right-8 text-white/10 text-8xl select-none">💕</div>
                  <div className="absolute bottom-2 left-4 text-white/10 text-6xl select-none">❤️</div>

                  <div className="flex flex-col md:flex-row items-center gap-8 relative">
                    {/* Score */}
                    <div className="text-center flex-shrink-0">
                      <div className="w-32 h-32 bg-white/20 rounded-full flex flex-col items-center justify-center mb-2">
                        <div className="text-4xl font-bold">{topMovies[0].score}%</div>
                        <Heart className="w-6 h-6 fill-current mt-1" />
                      </div>
                      <p className="text-sm opacity-80">Prescription Score</p>
                    </div>

                    {/* Movie info */}
                    <div className="flex-1">
                      <h2 className="text-3xl font-playfair font-bold mb-1">
                        {getMatchMessage(topMovies[0].score).title}
                      </h2>
                      <p className="text-lg opacity-90 mb-4">
                        {getMatchMessage(topMovies[0].score).message}
                      </p>
                      <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 flex gap-4 items-center">
                        {topMovies[0].movie.posterUrl ? (
                          <img
                            src={topMovies[0].movie.posterUrl}
                            alt={topMovies[0].movie.title}
                            className="w-16 h-24 object-cover rounded-xl shadow-lg flex-shrink-0"
                          />
                        ) : (
                          <div className="w-16 h-24 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                            <Film className="w-8 h-8" />
                          </div>
                        )}
                        <div>
                          <h3 className="text-xl font-bold">{topMovies[0].movie.title}</h3>
                          {topMovies[0].movie.releaseYear && (
                            <p className="opacity-75">({topMovies[0].movie.releaseYear})</p>
                          )}
                          {topMovies[0].movie.overview && (
                            <p className="opacity-80 text-sm mt-2 line-clamp-2">
                              {topMovies[0].movie.overview}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Top 3 matches */}
              <div className="mb-10">
                <h3 className="text-2xl font-playfair font-bold text-gray-800 mb-6 text-center">
                  Your Top 3 Prescriptions 💊💕
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {topMovies.map((item, index) => (
                    <motion.div
                      key={item.movie.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.15 }}
                      onClick={() => navigate(`/movies/${item.movie.id}`)}
                      className="bg-white rounded-2xl shadow-lg overflow-hidden cursor-pointer hover:shadow-xl transition-all hover:-translate-y-1 border border-gray-100"
                    >
                      {/* Poster */}
                      <div className="relative h-48 overflow-hidden">
                        {item.movie.posterUrl ? (
                          <img
                            src={item.movie.posterUrl}
                            alt={item.movie.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-romcom-pink to-romcom-purple flex items-center justify-center">
                            <Film className="w-12 h-12 text-white" />
                          </div>
                        )}
                        {/* Rank badge */}
                        <div className={`absolute top-3 left-3 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg ${
                          index === 0 ? 'bg-romcom-red' :
                          index === 1 ? 'bg-yellow-500' : 'bg-orange-400'
                        }`}>
                          {index + 1}
                        </div>
                        {/* Match score */}
                        <div className="absolute top-3 right-3 bg-black/50 text-white text-xs px-2 py-1 rounded-full backdrop-blur-sm">
                          {item.score}% match
                        </div>
                      </div>

                      <div className="p-4">
                        <h4 className="font-bold text-gray-800 mb-1 line-clamp-1">{item.movie.title}</h4>
                        {item.movie.releaseYear && (
                          <p className="text-gray-400 text-xs mb-2">({item.movie.releaseYear})</p>
                        )}
                        {item.movie.moods && item.movie.moods.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {item.movie.moods.slice(0, 2).map(mood => (
                              <span key={mood} className="text-xs bg-romcom-pink/20 text-romcom-red px-2 py-0.5 rounded-full capitalize">
                                {mood.replace('_', ' ')}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex flex-col sm:flex-row justify-center gap-4 mb-10">
                <button
                  onClick={() => setShowCard(true)}
                  className="btn-primary flex items-center justify-center gap-2"
                >
                  <Heart className="w-5 h-5 fill-current" />
                  Get My Prescription Card 💊💕
                </button>
                <button
                  onClick={restartPrescription}
                  className="btn-secondary flex items-center justify-center gap-2"
                >
                  <RefreshCw className="w-5 h-5" />
                  Get another prescription
                </button>
                <button
                  onClick={() => navigate('/movies')}
                  className="border-2 border-gray-300 text-gray-700 px-8 py-3 rounded-full font-medium hover:bg-gray-50 flex items-center justify-center gap-2"
                >
                  <Film className="w-5 h-5" />
                  Browse All Movies
                </button>
              </div>

              {/* Your answers recap */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                <h3 className="text-xl font-bold text-gray-800 mb-6">Your Answers</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {answers.map((answer, index) => {
                    const question = PRESCRIPTION_QUESTIONS.find(q => q.id === answer.questionId);
                    const option = question?.options.find(o => o.id === answer.optionId);
                    return (
                      <div key={index} className="border border-gray-100 rounded-xl p-4 bg-gray-50">
                        <div className="flex items-center mb-2">
                          <div className="w-6 h-6 bg-romcom-red/10 text-romcom-red rounded-full flex items-center justify-center text-xs font-bold mr-2 flex-shrink-0">
                            {index + 1}
                          </div>
                          <h4 className="font-medium text-gray-700 text-sm">{question?.question}</h4>
                        </div>
                        {option && (
                          <div className="flex items-center gap-2 ml-8">
                            <span className="text-xl">{option.emoji}</span>
                            <span className="text-gray-600 text-sm">{option.text}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Home Button */}
        <div className="text-center mt-12">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center text-gray-600 hover:text-romcom-red transition-colors"
          >
            <Home className="w-5 h-5 mr-2" />
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrescriptionPage;